import analytics from './analytics';

interface FetchOptions extends RequestInit {
  headers?: HeadersInit;
}

interface ApiResponse<T = unknown> {
  json: T;
  response: Response;
}

interface AnalyticsData {
  [key: string]: unknown;
}

interface ApiPayload<T = unknown> {
  payload?: T;
  analytics?: AnalyticsData | AnalyticsData[];
  messageToUser?: string;
  [key: string]: unknown;
}

const defaultFetchOptions: FetchOptions = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
};

function parseResponse<T>(response: Response): Promise<ApiResponse<T>> {
  return response.json().then((json: T) => ({ json, response }));
}

function handleUserMessages<T>(data: ApiResponse<T>): ApiResponse<T> {
  return data;
}

function handleAnalytics<T>(data: ApiResponse<ApiPayload<T>>): ApiResponse<ApiPayload<T>> {
  if (data.json.analytics) {
    analytics.send(data.json.analytics);
  }

  return data;
}

function handleNotOk<T>(data: ApiResponse<ApiPayload<T>>): ApiResponse<ApiPayload<T>> {
  const { json, response } = data;

  // NOTE: `response.ok` is true when the returned status is in the inclusive range 200-299.
  if (!response.ok && !json.messageToUser) {
    const error = new Error(response.statusText) as Error & { name: string };

    error.name = `${response.status} on ${response.url}`;

    throw error;
  }

  return data;
}

function handleResponse<T>(data: ApiResponse<ApiPayload<T>>): T {
  return data.json.payload || (data.json as unknown as T);
}

function handleFetchError(error: Error): Promise<never> {
  return Promise.reject(error);
}

function request<T = unknown>(url: string, options: FetchOptions): Promise<T> {
  return fetch(url, options)
    .then(response => parseResponse<ApiPayload<T>>(response))
    .then(handleUserMessages)
    .then(handleAnalytics)
    .then(handleNotOk)
    .then(handleResponse)
    .catch(handleFetchError);
}

function post<T = unknown>(endpoint: string, data: string): Promise<T> {
  return request<T>(
    endpoint,
    Object.assign({}, defaultFetchOptions, {
      body: data,
      method: 'post'
    })
  );
}

function toQueryString(queryObject: Record<string, unknown>): string {
  // Convert values to strings for URLSearchParams
  const stringRecord: Record<string, string> = {};
  Object.entries(queryObject).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      if (typeof value === 'object') {
        stringRecord[key] = JSON.stringify(value);
      } else if (typeof value === 'string') {
        stringRecord[key] = value;
      } else {
        stringRecord[key] = String(value as number | boolean);
      }
    }
  });
  const params = new URLSearchParams(stringRecord);
  const query = '?' + params.toString();
  return query;
}

function get<T = unknown>(endpoint: string, data?: Record<string, unknown>): Promise<T> {
  const queryString = data ? toQueryString(data) : '';

  return request<T>(endpoint + queryString, {
    ...defaultFetchOptions,
    method: 'get'
  });
}

function execute<T = unknown>(
  endpoint: string = '',
  data?: Record<string, unknown>,
  method?: 'get' | 'post'
): Promise<T> {
  if (method === 'get') {
    return get<T>(endpoint, data);
  } else {
    return post<T>(endpoint, JSON.stringify(data));
  }
}

export default {
  execute,
  get
};
